import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FilePlus2, FileText, LogOut, Mail, MessageCircle, Phone, Plus, Send, Trash2, Upload, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PRODUCT_TABS, TAB_BADGES } from "@/lib/data";

const emptyProduct = { tab: "ct", name: "", badge: "CT Scanner", status: "In Stock", image: "", details: { YOM: "" } };

const DEFAULT_TERMS = `Prices are in INR and exclusive of GST unless mentioned otherwise.
Delivery: 2-4 weeks from receipt of confirmed purchase order and advance payment.
Payment terms: 70% advance with order confirmation, 30% before dispatch / installation.
Installation & commissioning: OEM-supervised, included unless mentioned otherwise.
Warranty: 90 days on refurbished systems; extended AMC/CMC coverage available separately.
Transportation & insurance extra at actuals unless included above.
This quotation is valid for 15 days from the date of issue.`;

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n) || 0);

const blankQuote = () => ({
  id: null,
  enquiry_id: null,
  customer: { name: "", facility: "", email: "", phone: "", city: "" },
  items: [{ product: "", description: "", qty: 1, unit_price: "" }],
  gst_rate: 18,
  validity_days: 15,
  terms: DEFAULT_TERMS,
});

const waDigits = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  if (!d) return "";
  return d.length === 10 ? `91${d}` : d;
};

const waQuoteLink = (q) => {
  const phone = waDigits(q.customer.phone);
  if (!phone) return null;
  const lines = q.items.map(
    (it, i) => `${i + 1}. ${it.product} x ${it.qty} — ${inr(it.qty * it.unit_price)}`
  );
  const msg = [
    `*Quotation ${q.quote_no} — MEDIS Group LLP*`,
    ``,
    `Hi ${q.customer.name || ""}, please find your quotation:`,
    ``,
    ...lines,
    ``,
    `Subtotal: ${inr(q.subtotal)}`,
    `GST (${q.gst_rate}%): ${inr(q.gst_amount)}`,
    `*Grand Total: ${inr(q.grand_total)}*`,
    ``,
    `Validity: ${q.validity_days} days.`,
    `A detailed PDF copy can be shared on request.`,
    `Call +91 89565 28732 or reply here to confirm.`,
  ].join("\n");
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
};

const CATEGORY_MAP = [
  ["ct", "ct"],
  ["mri", "mri"],
  ["cath", "cathlab"],
  ["mammo", "mammo"],
  ["ultra", "ultrasound"],
];

const parseBulkLines = (text) => {
  const rows = [];
  const errors = [];
  text
    .split("\n")
    .map((l) => l.trim())
    .forEach((line, idx) => {
      if (!line || line.startsWith("#")) return;
      const parts = line.split("|").map((p) => p.trim());
      const [name, category = "", status = "In Stock", ...specParts] = parts;
      if (!name) {
        errors.push(`Line ${idx + 1}: missing product name`);
        return;
      }
      const catLow = category.toLowerCase();
      const tab = (CATEGORY_MAP.find(([k]) => catLow.includes(k)) || [null, "ct"])[1];
      const details = {};
      specParts.join("; ").split(";").forEach((s) => {
        const [k, ...rest] = s.split(":");
        if (k && k.trim() && rest.length) details[k.trim()] = rest.join(":").trim();
      });
      rows.push({
        tab,
        name,
        badge: TAB_BADGES[tab] || "CT Scanner",
        status: status.toLowerCase().includes("incom") ? "Incoming" : "In Stock",
        image: "",
        details,
        sort_order: 50 + idx,
      });
    });
  return { rows, errors };
};

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [quoteForm, setQuoteForm] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkReplace, setBulkReplace] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadEnquiries = () => api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  const loadQuotes = () => api.get("/admin/quotations").then((r) => setQuotes(r.data)).catch(() => {});
  const loadAll = () => {
    loadEnquiries();
    loadProducts();
    loadQuotes();
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadAll();
      })
      .catch(() => setUser(null));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      toast.success("Welcome back, " + (r.data.name || "Admin"));
      loadAll();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const saveProduct = async () => {
    if (!editing.name.trim()) {
      toast.error("Product name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing, badge: TAB_BADGES[editing.tab] || editing.badge, sort_order: editing.sort_order || 99 };
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing.id ? "Product updated." : "Product added.");
      setEditing(null);
      loadProducts();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    await api.delete(`/admin/products/${id}`).catch(() => toast.error("Delete failed."));
    toast.success("Product deleted.");
    loadProducts();
  };

  const runBulkImport = async () => {
    const { rows, errors } = parseBulkLines(bulkText);
    if (!rows.length) {
      toast.error("No valid product lines found. Check the format example.");
      return;
    }
    if (errors.length) toast.warning(errors.join("; "));
    if (bulkReplace && !window.confirm(`Replace all ${products.length} existing products with ${rows.length} imported ones?`)) return;
    setSaving(true);
    try {
      const res = await api.post("/admin/products/bulk", { products: rows, replace: bulkReplace });
      toast.success(`Imported ${res.data.count} products${bulkReplace ? " (replaced existing stock)" : ""}.`);
      setBulkOpen(false);
      setBulkText("");
      setBulkReplace(false);
      loadProducts();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const markContacted = async (id) => {
    await api.put(`/admin/enquiries/${id}/status`, { status: "contacted" }).catch(() => {});
    toast.success("Marked as contacted.");
    loadEnquiries();
  };

  // ---------- Quotations ----------

  const openQuoteFromEnquiry = (en) => {
    const matched = products.find((p) => en.product && p.badge && en.product.toLowerCase().includes(p.badge.toLowerCase().split(" ")[0]));
    const f = blankQuote();
    f.enquiry_id = en.id;
    f.customer = { name: en.name || "", facility: en.facility || "", email: en.email || "", phone: en.phone || "", city: en.city || "" };
    f.items = [
      {
        product: matched ? matched.name : en.product || "",
        description: matched ? Object.entries(matched.details || {}).map(([k, v]) => `${k}: ${v}`).join(" | ") : "",
        qty: 1,
        unit_price: "",
      },
    ];
    setQuoteForm(f);
  };

  const openQuoteEdit = (q) => {
    setQuoteForm({
      id: q.id,
      enquiry_id: q.enquiry_id || null,
      customer: { ...q.customer },
      items: q.items.map((i) => ({ ...i, unit_price: i.unit_price === 0 ? "" : String(i.unit_price) })),
      gst_rate: q.gst_rate,
      validity_days: q.validity_days,
      terms: q.terms,
    });
  };

  const quoteTotals = useMemo(() => {
    if (!quoteForm) return { sub: 0, gst: 0, grand: 0 };
    const sub = quoteForm.items.reduce((s, i) => s + (Number(i.qty) || 0) * (parseFloat(i.unit_price) || 0), 0);
    const gst = (sub * (Number(quoteForm.gst_rate) || 0)) / 100;
    return { sub, gst, grand: sub + gst };
  }, [quoteForm]);

  const setQuote = (k) => (e) => setQuoteForm((f) => ({ ...f, [k]: e.target.value }));
  const setCustomer = (k) => (e) => setQuoteForm((f) => ({ ...f, customer: { ...f.customer, [k]: e.target.value } }));
  const setItem = (idx, k, v) =>
    setQuoteForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [k]: v };
      if (k === "product") {
        const m = products.find((p) => p.name === v);
        if (m) items[idx].description = Object.entries(m.details || {}).map(([a, b]) => `${a}: ${b}`).join(" | ");
      }
      return { ...f, items };
    });

  const saveQuote = async () => {
    if (!quoteForm.customer.name.trim() || !quoteForm.customer.email.trim()) {
      toast.error("Customer name and email are required.");
      return;
    }
    const items = quoteForm.items.filter((i) => i.product.trim());
    if (!items.length) {
      toast.error("Add at least one line item.");
      return;
    }
    setSaving(true);
    const payload = {
      enquiry_id: quoteForm.enquiry_id,
      customer: quoteForm.customer,
      items: items.map((i) => ({ product: i.product, description: i.description, qty: Number(i.qty) || 1, unit_price: parseFloat(i.unit_price) || 0 })),
      gst_rate: Number(quoteForm.gst_rate) || 0,
      validity_days: Number(quoteForm.validity_days) || 15,
      terms: quoteForm.terms,
    };
    try {
      if (quoteForm.id) {
        await api.put(`/admin/quotations/${quoteForm.id}`, payload);
        toast.success("Quotation updated.");
      } else {
        const r = await api.post("/admin/quotations", payload);
        toast.success(`Quotation ${r.data.quote_no} created.`);
      }
      setQuoteForm(null);
      loadQuotes();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/admin/quotations/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${q.quote_no.replace(/\//g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed.");
    }
  };

  const sendQuote = async (q) => {
    if (!window.confirm(`Send quotation ${q.quote_no} to ${q.customer.email}?`)) return;
    try {
      await api.post(`/admin/quotations/${q.id}/send`);
      toast.success(`Quotation sent to ${q.customer.email}.`);
      loadQuotes();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const markQuoteStatus = async (q, status) => {
    try {
      await api.put(`/admin/quotations/${q.id}/status`, { status });
      toast.success(`Marked ${q.quote_no} as ${status}.`);
      loadQuotes();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Delete quotation ${q.quote_no}?`)) return;
    await api.delete(`/admin/quotations/${q.id}`).catch(() => {});
    toast.success("Quotation deleted.");
    loadQuotes();
  };

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-medis-subtle text-medis-gray text-sm">Checking session…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-medis-subtle px-5" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-2xl border border-medis-silver/30 shadow-xl p-8" data-testid="admin-login-form">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="MEDIS" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display font-bold text-medis-navy text-lg">MEDIS Admin</div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-medis-gray">Management Console</div>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-3">
            <label className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-medis-gray">Email</label>
            <input className="rounded-md border border-medis-silver/40 px-4 py-3 text-[14px] text-medis-navy focus:border-medis-teal focus:outline-none" data-testid="admin-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <label className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-medis-gray">Password</label>
            <input className="rounded-md border border-medis-silver/40 px-4 py-3 text-[14px] text-medis-navy focus:border-medis-teal focus:outline-none" data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            <button type="submit" className="mt-2 bg-medis-blue text-white py-3 rounded-md text-[14px] font-semibold hover:bg-medis-blue-dark transition-colors" data-testid="admin-login-submit">
              Sign In
            </button>
          </div>
        </form>
      </div>
    );
  }

  const dueQuotes = quotes.filter((q) => q.follow_up_due);

  return (
    <div className="min-h-screen bg-medis-subtle" data-testid="admin-dashboard">
      <header className="bg-medis-navy text-white">
        <div className="max-w-6xl mx-auto px-5 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="MEDIS" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display font-bold text-[15px]">MEDIS Admin</div>
              <div className="text-[10.5px] uppercase tracking-[0.16em] text-white/50">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-[13px] text-white/80 hover:text-medis-teal" data-testid="admin-view-site-link">
              View Site
            </a>
            <button onClick={logout} className="inline-flex items-center gap-2 text-[13px] text-white/80 hover:text-medis-teal" data-testid="admin-logout">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "enquiries", label: `Enquiries (${enquiries.length})` },
            { id: "quotes", label: `Quotations (${quotes.length})${dueQuotes.length ? ` · ${dueQuotes.length} due` : ""}`, alert: dueQuotes.length > 0 },
            { id: "products", label: `Products (${products.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="px-5 py-2.5 rounded-full text-[13px] font-semibold border transition-all"
              style={
                tab === t.id
                  ? { background: "#0b3c82", color: "white", borderColor: "#0b3c82" }
                  : t.alert
                  ? { background: "#fffbeb", color: "#b45309", borderColor: "#fcd34d" }
                  : { background: "white", color: "#5f6b7a", borderColor: "#e2e8f0" }
              }
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="flex flex-col gap-3" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && <div className="bg-white rounded-xl border border-medis-silver/30 p-8 text-center text-medis-gray text-sm">No enquiries yet. Submissions from the website appear here.</div>}
            {enquiries.map((en) => (
              <div key={en.id} className="bg-white rounded-xl border border-medis-silver/30 p-5" data-testid={`admin-enquiry-${en.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-semibold text-medis-navy">
                      {en.name}
                      {en.status === "contacted" && <span className="ml-2 text-[10px] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-semibold">CONTACTED</span>}
                      {en.status === "quoted" && <span className="ml-2 text-[10px] bg-blue-50 text-medis-blue px-2 py-0.5 rounded-full font-semibold">QUOTED</span>}
                    </div>
                    <div className="text-[12px] text-medis-gray mt-0.5">
                      {en.facility || "—"} · {en.city || "—"} · {en.product}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => openQuoteFromEnquiry(en)} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-medis-blue px-3 py-2 rounded-md hover:bg-medis-blue-dark" data-testid={`admin-enquiry-quote-${en.id}`}>
                      <FilePlus2 className="h-3.5 w-3.5" /> Create Quote
                    </button>
                    <button onClick={() => markContacted(en.id)} disabled={en.status === "contacted"} className="text-[12px] font-semibold text-medis-blue hover:text-medis-teal disabled:opacity-40" data-testid={`admin-enquiry-contact-${en.id}`}>
                      Mark contacted
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-[13.5px] text-medis-gray leading-relaxed">{en.message}</p>
                <div className="mt-3 flex flex-wrap gap-4 text-[12.5px] text-medis-gray">
                  <a className="flex items-center gap-1.5 hover:text-medis-blue" href={`tel:${en.phone}`}>
                    <Phone className="h-3.5 w-3.5" /> {en.phone}
                  </a>
                  <a className="flex items-center gap-1.5 hover:text-medis-blue" href={`mailto:${en.email}`}>
                    <Mail className="h-3.5 w-3.5" /> {en.email}
                  </a>
                  <span className="text-medis-silver">{new Date(en.created_at).toLocaleString("en-IN")}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "quotes" && (
          <div data-testid="admin-quotes-list">
            <div className="flex justify-end mb-4">
              <button onClick={() => setQuoteForm(blankQuote())} className="inline-flex items-center gap-2 bg-medis-blue text-white px-5 py-2.5 rounded-md text-[13px] font-semibold hover:bg-medis-blue-dark" data-testid="admin-new-quote">
                <Plus className="h-4 w-4" /> New Quotation
              </button>
            </div>
            {dueQuotes.length > 0 && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3.5 flex items-center gap-3" data-testid="followup-banner">
                <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
                <span className="text-[13px] font-semibold text-amber-800">
                  {dueQuotes.length} quotation{dueQuotes.length > 1 ? "s" : ""} sent 7+ days ago {dueQuotes.length > 1 ? "still need" : "still needs"} follow-up. No reply recorded.
                </span>
              </div>
            )}
            {quotes.length === 0 && (
              <div className="bg-white rounded-xl border border-medis-silver/30 p-8 text-center text-medis-gray text-sm">
                No quotations yet. Create one from an enquiry or start fresh.
              </div>
            )}
            <div className="flex flex-col gap-2">
              {quotes.map((q) => (
                <div key={q.id} className={`bg-white rounded-xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3 ${q.follow_up_due ? "border-amber-300" : "border-medis-silver/30"}`} data-testid={`admin-quote-${q.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[12px] font-bold text-medis-blue">{q.quote_no}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${q.status === "sent" ? "bg-emerald-50 text-emerald-600" : q.status === "replied" ? "bg-cyan-50 text-cyan-700" : q.status === "won" ? "bg-blue-50 text-medis-blue" : q.status === "lost" ? "bg-red-50 text-red-500" : "bg-gray-100 text-gray-500"}`}>
                        {q.status.toUpperCase()}
                      </span>
                      {q.follow_up_due && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`admin-quote-due-${q.id}`}>
                          FOLLOW-UP DUE · {q.days_since_sent}D
                        </span>
                      )}
                    </div>
                    <div className="font-display font-semibold text-medis-navy truncate mt-0.5">
                      {q.customer.name} {q.customer.facility ? `· ${q.customer.facility}` : ""}
                    </div>
                    <div className="text-[12px] text-medis-gray mt-0.5">
                      {inr(q.grand_total)} · {new Date(q.created_at).toLocaleDateString("en-IN")}
                      {q.sent_to ? ` · sent to ${q.sent_to}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                    {q.status === "sent" && (
                      <button onClick={() => markQuoteStatus(q, "replied")} className="text-[11.5px] font-semibold text-cyan-700 border border-cyan-200 bg-cyan-50 rounded-md px-2.5 py-1.5 hover:bg-cyan-100" data-testid={`admin-quote-reply-${q.id}`}>
                        Reply Received
                      </button>
                    )}
                    {["sent", "replied"].includes(q.status) && (
                      <>
                        <button onClick={() => markQuoteStatus(q, "won")} className="text-[11.5px] font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-md px-2.5 py-1.5 hover:bg-emerald-100" data-testid={`admin-quote-won-${q.id}`}>
                          Mark Won
                        </button>
                        <button onClick={() => markQuoteStatus(q, "lost")} className="text-[11.5px] font-semibold text-red-600 border border-red-200 bg-red-50 rounded-md px-2.5 py-1.5 hover:bg-red-100" data-testid={`admin-quote-lost-${q.id}`}>
                          Mark Lost
                        </button>
                      </>
                    )}
                    {waQuoteLink(q) && (
                      <a href={waQuoteLink(q)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-green-700 border border-green-200 bg-green-50 rounded-md px-2.5 py-1.5 hover:bg-green-100" data-testid={`admin-quote-whatsapp-${q.id}`}>
                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                      </a>
                    )}
                    <button onClick={() => openQuoteEdit(q)} className="text-[12px] font-semibold text-medis-blue hover:text-medis-teal" data-testid={`admin-quote-edit-${q.id}`}>
                      Edit
                    </button>
                    <button onClick={() => downloadPdf(q)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-medis-navy border border-medis-silver/40 rounded-md px-2.5 py-1.5 hover:border-medis-teal hover:text-medis-teal" data-testid={`admin-quote-pdf-${q.id}`}>
                      <FileText className="h-3.5 w-3.5" /> PDF
                    </button>
                    <button onClick={() => sendQuote(q)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-medis-blue rounded-md px-2.5 py-1.5 hover:bg-medis-blue-dark" data-testid={`admin-quote-send-${q.id}`}>
                      <Send className="h-3.5 w-3.5" /> Send
                    </button>
                    <button onClick={() => deleteQuote(q)} className="text-medis-gray hover:text-red-500" data-testid={`admin-quote-delete-${q.id}`} aria-label="Delete quotation">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products-list">
            <div className="flex justify-end gap-2 mb-4">
              <button onClick={() => setBulkOpen(true)} className="inline-flex items-center gap-2 border border-medis-blue/30 text-medis-blue px-5 py-2.5 rounded-md text-[13px] font-semibold hover:bg-medis-blue hover:text-white transition-colors" data-testid="admin-bulk-import">
                <Upload className="h-4 w-4" /> Bulk Import
              </button>
              <button onClick={() => setEditing({ ...emptyProduct })} className="inline-flex items-center gap-2 bg-medis-blue text-white px-5 py-2.5 rounded-md text-[13px] font-semibold hover:bg-medis-blue-dark" data-testid="admin-add-product">
                <Plus className="h-4 w-4" /> Add Product
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {products.map((p) => (
                <div key={p.id} className="bg-white rounded-xl border border-medis-silver/30 px-5 py-4 flex items-center justify-between gap-4" data-testid={`admin-product-${p.id}`}>
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-medis-blue">{p.badge}</div>
                    <div className="font-display font-semibold text-medis-navy truncate">{p.name}</div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-[10.5px] font-semibold px-2.5 py-1 rounded-full ${p.status === "In Stock" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{p.status}</span>
                    <button onClick={() => setEditing({ ...p, details: { ...p.details } })} className="text-[12px] font-semibold text-medis-blue hover:text-medis-teal" data-testid={`admin-product-edit-${p.id}`}>
                      Edit
                    </button>
                    <button onClick={() => deleteProduct(p.id)} className="text-medis-gray hover:text-red-500" data-testid={`admin-product-delete-${p.id}`} aria-label="Delete product">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" data-testid="bulk-import-modal">
          <div className="bg-white w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-6 sm:p-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-medis-navy text-lg">Bulk Import Products</h3>
                <p className="text-[12px] text-medis-gray mt-0.5">Paste your stock list — one machine per line.</p>
              </div>
              <button onClick={() => setBulkOpen(false)} className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-medis-subtle" data-testid="bulk-import-close" aria-label="Close">
                <X className="h-4 w-4 text-medis-gray" />
              </button>
            </div>
            <div className="mt-4 rounded-lg bg-medis-subtle/70 border border-medis-silver/30 p-3 text-[11.5px] text-medis-gray font-mono leading-relaxed">
              Format: Name | Category | Status | Specs
              <br />
              Canon Aquilion Prime 160 | CT Scanners | In Stock | YOM: 2019; Slices: 160
              <br />
              SIEMENS Magnetom Avanto 1.5T | MRI Scanners | In Stock | Field: 1.5T; Coils: 18ch
            </div>
            <textarea
              rows="8"
              className="mt-4 w-full border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy font-mono leading-relaxed"
              placeholder="Paste lines here…"
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              data-testid="bulk-import-textarea"
            />
            <label className="mt-3 flex items-center gap-2 text-[12.5px] text-medis-gray cursor-pointer">
              <input type="checkbox" checked={bulkReplace} onChange={(e) => setBulkReplace(e.target.checked)} data-testid="bulk-import-replace" className="h-4 w-4 accent-[#0b3c82]" />
              Replace all existing products (use this to swap sample stock for your real inventory)
            </label>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setBulkOpen(false)} className="px-5 py-2.5 rounded-md text-[13px] font-semibold text-medis-gray hover:bg-medis-subtle" data-testid="bulk-import-cancel">
                Cancel
              </button>
              <button onClick={runBulkImport} disabled={saving} className="px-6 py-2.5 rounded-md text-[13px] font-semibold bg-medis-blue text-white hover:bg-medis-blue-dark disabled:opacity-60" data-testid="bulk-import-submit">
                {saving ? "Importing…" : "Import Products"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" data-testid="admin-product-modal">
          <div className="bg-white w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-6">
            <h3 className="font-display font-bold text-medis-navy text-lg">{editing.id ? "Edit Product" : "Add Product"}</h3>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-medis-gray mb-1 block">Category</label>
                <select
                  className="w-full border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] font-semibold text-medis-navy"
                  value={editing.tab}
                  onChange={(e) => setEditing((p) => ({ ...p, tab: e.target.value, badge: TAB_BADGES[e.target.value] }))}
                  data-testid="admin-product-category"
                >
                  {PRODUCT_TABS.filter((t) => t.id !== "all").map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-medis-gray mb-1 block">Availability</label>
                <select className="w-full border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] font-semibold text-medis-navy" value={editing.status} onChange={(e) => setEditing((p) => ({ ...p, status: e.target.value }))} data-testid="admin-product-status">
                  <option>In Stock</option>
                  <option>Incoming</option>
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-medis-gray mb-1 block">Product Name *</label>
              <input className="w-full border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy" value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. SIEMENS Somatom Definition AS" data-testid="admin-product-name" />
            </div>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-medis-gray mb-1 block">Image URL</label>
              <input className="w-full border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy" value={editing.image} onChange={(e) => setEditing((p) => ({ ...p, image: e.target.value }))} placeholder="/c1.jpeg" data-testid="admin-product-image" />
            </div>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-medis-gray mb-1 block">Specs (KEY = VALUE per line)</label>
              <textarea
                rows="4"
                className="w-full border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy"
                value={Object.entries(editing.details || {}).map(([k, v]) => `${k} = ${v}`).join("\n")}
                onChange={(e) => {
                  const details = {};
                  e.target.value.split("\n").forEach((line) => {
                    const [k, ...rest] = line.split("=");
                    if (k && k.trim()) details[k.trim()] = rest.join("=").trim();
                  });
                  setEditing((p) => ({ ...p, details }));
                }}
                data-testid="admin-product-details"
              />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-md text-[13px] font-semibold text-medis-gray hover:bg-medis-subtle" data-testid="admin-product-cancel">
                Cancel
              </button>
              <button onClick={saveProduct} disabled={saving} className="px-5 py-2.5 rounded-md text-[13px] font-semibold bg-medis-blue text-white hover:bg-medis-blue-dark disabled:opacity-60" data-testid="admin-product-save">
                {saving ? "Saving…" : "Save Product"}
              </button>
            </div>
          </div>
        </div>
      )}

      {quoteForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" data-testid="quote-builder-modal">
          <div className="bg-white w-full sm:max-w-3xl max-h-[94vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-6 sm:p-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-medis-navy text-lg">{quoteForm.id ? "Edit Quotation" : "New Quotation"}</h3>
                <p className="text-[12px] text-medis-gray mt-0.5">Quote number is generated automatically on save.</p>
              </div>
              <button onClick={() => setQuoteForm(null)} className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-medis-subtle" data-testid="quote-builder-close" aria-label="Close">
                <X className="h-4 w-4 text-medis-gray" />
              </button>
            </div>

            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-medis-teal mb-2">Customer</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy" placeholder="Customer name *" value={quoteForm.customer.name} onChange={setCustomer("name")} data-testid="quote-customer-name" />
                <input className="border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy" placeholder="Hospital / Facility" value={quoteForm.customer.facility} onChange={setCustomer("facility")} data-testid="quote-customer-facility" />
                <input className="border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy" placeholder="Customer email *" type="email" value={quoteForm.customer.email} onChange={setCustomer("email")} data-testid="quote-customer-email" />
                <input className="border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy" placeholder="Phone" value={quoteForm.customer.phone} onChange={setCustomer("phone")} data-testid="quote-customer-phone" />
                <input className="border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy" placeholder="City" value={quoteForm.customer.city} onChange={setCustomer("city")} data-testid="quote-customer-city" />
              </div>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-medis-teal">Line Items</div>
                <button onClick={() => setQuoteForm((f) => ({ ...f, items: [...f.items, { product: "", description: "", qty: 1, unit_price: "" }] }))} className="inline-flex items-center gap-1 text-[12px] font-semibold text-medis-blue hover:text-medis-teal" data-testid="quote-add-item">
                  <Plus className="h-3.5 w-3.5" /> Add line
                </button>
              </div>
              <datalist id="quote-product-names">
                {products.map((p) => (
                  <option key={p.id} value={p.name} />
                ))}
              </datalist>
              <div className="flex flex-col gap-2">
                {quoteForm.items.map((it, idx) => (
                  <div key={idx} className="rounded-lg border border-medis-silver/30 p-3 grid grid-cols-12 gap-2 items-center bg-medis-subtle/40" data-testid={`quote-item-${idx}`}>
                    <input list="quote-product-names" className="col-span-12 sm:col-span-5 border border-medis-silver/40 rounded-lg px-3 py-2 text-[13px] text-medis-navy bg-white" placeholder="Product / service name *" value={it.product} onChange={(e) => setItem(idx, "product", e.target.value)} data-testid={`quote-item-product-${idx}`} />
                    <input className="col-span-12 sm:col-span-4 border border-medis-silver/40 rounded-lg px-3 py-2 text-[12px] text-medis-navy bg-white" placeholder="Specs / description (auto-fills on match)" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} data-testid={`quote-item-desc-${idx}`} />
                    <input className="col-span-6 sm:col-span-1 border border-medis-silver/40 rounded-lg px-2 py-2 text-[13px] text-medis-navy bg-white text-center" type="number" min="1" value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} data-testid={`quote-item-qty-${idx}`} />
                    <input className="col-span-10 sm:col-span-1 border border-medis-silver/40 rounded-lg px-2 py-2 text-[13px] text-medis-navy bg-white" type="number" min="0" placeholder="Unit price (INR)" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} data-testid={`quote-item-price-${idx}`} />
                    <button onClick={() => setQuoteForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} className="col-span-2 sm:col-span-1 justify-self-end text-medis-gray hover:text-red-500" data-testid={`quote-item-remove-${idx}`} aria-label="Remove item">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-medis-gray mb-1 block">GST %</label>
                <input type="number" min="0" className="w-full border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy" value={quoteForm.gst_rate} onChange={setQuote("gst_rate")} data-testid="quote-gst" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-medis-gray mb-1 block">Validity (days)</label>
                <input type="number" min="1" className="w-full border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[13px] text-medis-navy" value={quoteForm.validity_days} onChange={setQuote("validity_days")} data-testid="quote-validity" />
              </div>
              <div className="rounded-lg border border-medis-silver/30 bg-medis-subtle/60 p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-medis-gray">Totals</div>
                <div className="text-[12px] text-medis-gray mt-1">Subtotal {inr(quoteTotals.sub)}</div>
                <div className="text-[12px] text-medis-gray">GST {inr(quoteTotals.gst)}</div>
                <div className="text-[14px] font-bold text-medis-blue mt-0.5">Grand Total {inr(quoteTotals.grand)}</div>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-widest font-bold text-medis-gray mb-1 block">Terms & Conditions</label>
              <textarea rows="5" className="w-full border border-medis-silver/40 rounded-lg px-3 py-2.5 text-[12.5px] text-medis-navy leading-relaxed" value={quoteForm.terms} onChange={setQuote("terms")} data-testid="quote-terms" />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setQuoteForm(null)} className="px-5 py-2.5 rounded-md text-[13px] font-semibold text-medis-gray hover:bg-medis-subtle" data-testid="quote-cancel">
                Cancel
              </button>
              <button onClick={saveQuote} disabled={saving} className="px-6 py-2.5 rounded-md text-[13px] font-semibold bg-medis-blue text-white hover:bg-medis-blue-dark disabled:opacity-60" data-testid="quote-save">
                {saving ? "Saving…" : "Save Quotation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
